// EINGEFROREN 30.10.2026 (Ballast-Abbau P8), bei Bedarf reaktivieren: Route ausgeblendet bzw. API-Weg 404 (`FROZEN_TENANT_ROUTES`).
/**
 * Live-Vorschau je Angebot im Blockmodus (nur dev/Sandbox): erst auf ausdrücklichen Klick, weil jede Vorschau einen
 * Lesezugriff des Tageskontingents verbraucht. Zeigt die aktuelle Beschreibung bei eBay mit den Galerieblöcken, wie PAT
 * sie austauschen würde, im selben Sandbox-iframe wie die Editoren. Bei eBay wird dabei nichts geändert.
 */
import { Alert, Button, Group, Loader, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiRequestError } from '../api/client.js';
import { useServices } from '../services.js';
import { dateTime, t } from '../texts.js';
import { Preview } from './render-preview.js';

const ITEM_ID = /^[1-9][0-9]{0,19}$/;

function LiveBlockPreviewDialog({ tenantId, ebayItemId, opened, onClose }: { tenantId: string; ebayItemId: string; opened: boolean; onClose: () => void }) {
  const { api } = useServices();
  const load = useMutation({ mutationFn: () => api.liveBlockPreview(tenantId, ebayItemId) });
  const close = () => {
    // Beim nächsten Öffnen nie das alte Ergebnis oder den alten Fehler zeigen.
    load.reset();
    onClose();
  };
  return (
    <Modal opened={opened} onClose={close} title={`${t('liveBlockPreview.title')}: ${ebayItemId}`} size="xl">
      <Stack gap="sm">
        <Text size="sm">{t('liveBlockPreview.intro')}</Text>
        <Text size="xs" c="dimmed">
          {t('liveBlockPreview.quota')}
        </Text>
        {load.isPending ? (
          <Loader size="sm" />
        ) : load.isSuccess ? (
          <Preview result={{ html: load.data.html, warnings: load.data.warnings }}>
            <Stack gap={2}>
              <Text size="sm" fw={500}>
                {load.data.blockCount === 0 && load.data.unchanged
                  ? t('liveBlockPreview.noBlocks')
                  : t(load.data.unchanged ? 'liveBlockPreview.unchanged' : 'liveBlockPreview.changed', { count: load.data.blockCount })}
              </Text>
              <Text size="xs" c="dimmed">
                {t('liveBlockPreview.result', { date: dateTime(load.data.observedAt) })}
              </Text>
            </Stack>
          </Preview>
        ) : (
          <>
            {load.isError && (
              <Alert color="yellow" role="alert">
                {load.error instanceof ApiRequestError && load.error.detail ? load.error.detail : t('common.error')}
              </Alert>
            )}
            <Group>
              <Button onClick={() => load.mutate()}>{t('liveBlockPreview.load')}</Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

/** Knopf je Angebot (Angebotsliste, nur Blockmodus und Bearbeiter). */
export function LiveBlockPreviewButton({ tenantId, ebayItemId }: { tenantId: string; ebayItemId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="compact-xs" variant="subtle" onClick={() => setOpen(true)} aria-label={`${t('liveBlockPreview.open')}: ${ebayItemId}`}>
        {t('liveBlockPreview.open')}
      </Button>
      {open && <LiveBlockPreviewDialog tenantId={tenantId} ebayItemId={ebayItemId} opened onClose={() => setOpen(false)} />}
    </>
  );
}

/** Eingabe einer Angebotsnummer (Live-Update-Seite, „Test/Vorschau je Artikelnummer“). */
export function LiveBlockPreviewByNumber({ tenantId, editable }: { tenantId: string; editable: boolean }) {
  const [value, setValue] = useState('');
  const [shown, setShown] = useState<string | undefined>();
  const trimmed = value.trim();
  const invalid = trimmed !== '' && !ITEM_ID.test(trimmed);
  return (
    <Stack gap="sm">
      <Text fw={500}>{t('liveBlockPreview.section')}</Text>
      <Text size="sm">{t('liveBlockPreview.sectionIntro')}</Text>
      {editable ? (
        <Group align="flex-end">
          <TextInput
            label={t('liveBlockPreview.itemId')}
            value={value}
            onChange={e => setValue(e.currentTarget.value)}
            error={invalid ? t('liveBlockPreview.itemIdInvalid') : undefined}
            inputMode="numeric"
          />
          <Button disabled={trimmed === '' || invalid} onClick={() => setShown(trimmed)}>
            {t('liveBlockPreview.open')}
          </Button>
        </Group>
      ) : (
        <Text size="xs" c="dimmed">
          {t('liveBlockPreview.editorsOnly')}
        </Text>
      )}
      {shown && <LiveBlockPreviewDialog key={shown} tenantId={tenantId} ebayItemId={shown} opened onClose={() => setShown(undefined)} />}
    </Stack>
  );
}
